import type { ReactNode } from "react";
import clsx from "clsx";
import type { CurrentUser } from "@/lib/auth";
import { userIsBuero } from "@/lib/auth";
import { isHourBookingBlockedStatus } from "@/lib/material";
import type { ProjectOverviewBundle } from "@/lib/projectOverview";
import {
  userCanCreateMaterialRequestQr,
  userCanCreateMaterialRequestsFromOverview,
  userCanCreateOrdersFromOverview,
  userCanCreateTimesheetsFromOverview,
  userCanManageHeroFieldsInOverview,
  userCanManageLinkedAssets,
  userCanViewBueroProjectOverviewSections,
  userCanViewProjectFotos,
} from "@/projectOverview/permissions";
import { OverviewCard } from "@/projectOverview/OverviewCard";
import { OverviewMasonry } from "@/projectOverview/OverviewMasonry";
import { ProjectInfoSection } from "@/projectOverview/sections/ProjectInfoSection";
import { MontageplanScheduleSection } from "@/projectOverview/sections/MontageplanScheduleSection";
import { CoverPrintSection } from "@/projectOverview/sections/CoverPrintSection";
import { LateHourPermissionsSection } from "@/projectOverview/sections/LateHourPermissionsSection";
import { TimesheetsSection } from "@/projectOverview/sections/TimesheetsSection";
import { BookedHoursSection } from "@/projectOverview/sections/BookedHoursSection";
import { AppDocumentsSection } from "@/projectOverview/sections/AppDocumentsSection";
import { HeroDocumentsSection } from "@/projectOverview/sections/HeroDocumentsSection";
import { OrdersSection } from "@/projectOverview/sections/OrdersSection";
import { TagsSection } from "@/projectOverview/sections/TagsSection";
import { HeroCustomFieldsSection } from "@/projectOverview/sections/HeroCustomFieldsSection";
import { NotesSection } from "@/projectOverview/sections/NotesSection";
import { UnterlagenOverviewSection } from "@/projectOverview/sections/UnterlagenOverviewSection";
import { ProjectFotosSection } from "@/projectOverview/sections/ProjectFotosSection";
import { OfferPositionsSection } from "@/projectOverview/sections/OfferPositionsSection";
import { LinkedAssetsSection } from "@/projectOverview/sections/LinkedAssetsSection";
import { MaterialRequestsSection } from "@/projectOverview/sections/MaterialRequestsSection";
import type { ProjectOverviewReplyDraft } from "@/projectOverview/projectOverviewContextValue";

/** Wrapper hält Fragmente (Karte + Dialog) als eine Kachel zusammen. */
function OverviewTile({ children }: { children: ReactNode }) {
  return <div className="eh-po-masonry__tile">{children}</div>;
}

export function buildProjectOverviewPanels({
  data,
  user,
  groups,
  onRefetch,
  onEdit,
  forceBueroSections = false,
  includeOrders = true,
  includeHeroDocuments = true,
  includeAppDocuments = true,
  pageLayout = false,
  highlightNoteId = null,
  replyDraft = null,
  openMontageunterlagen = false,
  stuecklisteId = null,
  onStuecklisteEditorOpenChange,
}: {
  data: ProjectOverviewBundle;
  user: CurrentUser | null;
  groups: string[];
  onRefetch: () => void;
  onEdit: () => void;
  /** Admin-Projektseite: Büro-Bereiche immer anzeigen. */
  forceBueroSections?: boolean;
  /** Kompakte Übersicht ohne Bestellungen (eigener Tab auf der Projektseite). */
  includeOrders?: boolean;
  /** Hero-Dokumente in der Übersicht (auf der Projektseite eigener Tab). */
  includeHeroDocuments?: boolean;
  /** App-Dokumente in der Übersicht (auf der Projektseite eigener Tab). */
  includeAppDocuments?: boolean;
  /** Breiteres Grid auf der Projektseite (ein Grid, kein verschachteltes Layout). */
  pageLayout?: boolean;
  highlightNoteId?: number | null;
  replyDraft?: ProjectOverviewReplyDraft | null;
  openMontageunterlagen?: boolean;
  stuecklisteId?: number | null;
  onStuecklisteEditorOpenChange?: (open: boolean) => void;
}): { main: ReactNode; notes: ReactNode } {
  const isBuero = userIsBuero(user);
  const canViewBueroSections =
    forceBueroSections || userCanViewBueroProjectOverviewSections(user);
  const canViewFotos = canViewBueroSections || userCanViewProjectFotos(user, groups);
  const canCreateMaQr = forceBueroSections || userCanCreateMaterialRequestQr(user);
  const canCreateMa = userCanCreateMaterialRequestsFromOverview(user, groups);
  const canCreateOrder = forceBueroSections || userCanCreateOrdersFromOverview(user);
  const canCreateTs = userCanCreateTimesheetsFromOverview(user, groups);
  const canEditHero = userCanManageHeroFieldsInOverview(user, groups);
  const canManageLinkedAssets = userCanManageLinkedAssets(user, groups);
  const showLateHourPermissions =
    isBuero &&
    String(data.project.workflow_status || "").trim().toLowerCase() !== "abgelehnt" &&
    (Boolean(data.project.project_completed) ||
      isHourBookingBlockedStatus(data.project.workflow_status));

  const main = (
    <OverviewMasonry
      pageLayout={pageLayout}
      className={clsx(pageLayout ? "eh-po-grid--page-tab" : "eh-po-grid--modal")}
    >
      <OverviewTile key="project-info">
        <ProjectInfoSection
          project={data.project}
          canEdit={isBuero}
          canEditReferenz
          canMuendlichBeauftragt={canCreateTs}
          onEdit={onEdit}
          onSaved={onRefetch}
          user={user}
        />
      </OverviewTile>
      <OverviewTile key="montageplan">
        <MontageplanScheduleSection orderId={data.project.id} />
      </OverviewTile>
      {canCreateMa || canCreateMaQr ? (
        <OverviewTile key="material-requests">
          <MaterialRequestsSection
            project={data.project}
            canCreate={canCreateMa}
            canCreateQr={canCreateMaQr}
            user={user}
            onChanged={onRefetch}
          />
        </OverviewTile>
      ) : null}
      <OverviewTile key="linked-assets">
        <LinkedAssetsSection project={data.project} canEdit={canManageLinkedAssets} />
      </OverviewTile>
      <OverviewTile key="offers">
        <OfferPositionsSection project={data.project} />
      </OverviewTile>
      <OverviewTile key="unterlagen">
        <UnterlagenOverviewSection
          orderId={data.project.id}
          auftragsnummer={data.project.auftragsnummer}
          user={user}
          groups={groups}
          initialOpen={openMontageunterlagen}
          initialStuecklisteId={stuecklisteId}
          onChanged={onRefetch}
          onStuecklisteEditorOpenChange={onStuecklisteEditorOpenChange}
        />
      </OverviewTile>
      {canViewFotos ? (
        <OverviewTile key="project-fotos">
          <ProjectFotosSection orderId={data.project.id} />
        </OverviewTile>
      ) : null}
      <OverviewTile key="cover-print">
        <OverviewCard title="Deckblatt drucken">
          <CoverPrintSection projectId={data.project.id} hideHeading />
        </OverviewCard>
      </OverviewTile>
      {showLateHourPermissions ? (
        <OverviewTile key="late-hour-permissions">
          <LateHourPermissionsSection project={data.project} />
        </OverviewTile>
      ) : null}
      <OverviewTile key="timesheets">
        <TimesheetsSection
          project={data.project}
          timesheets={data.timesheets}
          schadensmeldungen={data.schadensmeldungen}
          canCreate={canCreateTs}
          variant={pageLayout ? "timesheets" : "default"}
        />
      </OverviewTile>
      <OverviewTile key="booked-hours">
        <BookedHoursSection
          project={data.project}
          canRebook={isBuero}
          onChanged={onRefetch}
        />
      </OverviewTile>
      {canViewBueroSections && includeAppDocuments ? (
        <OverviewTile key="app-documents">
          <AppDocumentsSection orderId={data.project.id} />
        </OverviewTile>
      ) : null}
      {canViewBueroSections && includeHeroDocuments ? (
        <OverviewTile key="hero-documents">
          <HeroDocumentsSection orderId={data.project.id} />
        </OverviewTile>
      ) : null}
      {canViewBueroSections && includeOrders ? (
        <OverviewTile key="orders">
          <OrdersSection
            project={data.project}
            orders={data.orders}
            canCreate={canCreateOrder}
            onChanged={onRefetch}
          />
        </OverviewTile>
      ) : null}
      <OverviewTile key="tags">
        <TagsSection
          orderId={data.project.id}
          tags={data.tags}
          suggestions={data.tagSuggestions}
          onChanged={onRefetch}
        />
      </OverviewTile>
      <OverviewTile key="hero-custom-fields">
        <HeroCustomFieldsSection
          project={data.project}
          canEdit={canEditHero}
          onSaved={onRefetch}
        />
      </OverviewTile>
    </OverviewMasonry>
  );

  const notes = (
    <NotesSection
      orderId={data.project.id}
      project={data.project}
      notes={data.notes}
      user={user}
      isBuero={isBuero}
      onChanged={onRefetch}
      highlightNoteId={highlightNoteId}
      replyDraft={replyDraft}
    />
  );

  return { main, notes };
}
